import { useState } from 'react'
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

/**
 * Tabs. The strip is built from the child nodes, and the active index lives on
 * the wrapper so CSS can reveal one panel — the panels themselves are real
 * editable children, so their content is never hidden from the document.
 *
 * In Markdown this is `::::tabs` around `:::tab`, where the outer container needs
 * MORE colons than the inner. That is precisely the detail a writer should never
 * have to think about, which is why this block exists.
 */
export function TabsView({ node, editor, getPos, updateAttributes }: NodeViewProps) {
  const titles: string[] = []
  /**
   * Which tab a reader will see first.
   *
   * The editor opens on it too. Showing the writer the first tab while the
   * published page opens on the third is the kind of difference nobody
   * notices until a reader reports it.
   */
  let opensOn = 0
  node.forEach((child, _offset, index) => {
    titles.push((child.attrs['title'] as string | null) || `Tab ${index + 1}`)
    if (child.attrs['default'] === true && opensOn === 0) opensOn = index
  })
  const [active, setActive] = useState(opensOn)
  const current = Math.min(active, Math.max(0, titles.length - 1))

  const addTab = () => {
    const pos = getPos?.()
    if (pos === undefined) return
    const end = pos + node.nodeSize - 1
    editor
      .chain()
      .focus()
      .insertContentAt(end, {
        type: 'tab',
        attrs: { title: `Tab ${titles.length + 1}` },
        content: [{ type: 'paragraph' }],
      })
      .run()
    setActive(titles.length)
  }

  return (
    <NodeViewWrapper
      className="be-tabs"
      data-sls="tabs"
      data-active={current}
      /**
       * Whether this block is synchronised, as an attribute rather than as
       * React state passed down.
       *
       * A node view re-renders when ITS node changes, not when its parent's
       * attributes do — so a tab asking the document for its parent's group
       * showed the right answer only after something else happened to redraw
       * it. Measured: naming the block did nothing until the tab was clicked.
       * CSS reads this and reveals the value fields, which needs no render at
       * all.
       */
      data-sync={(node.attrs['group'] as string | null) ? 'on' : undefined}
    >
      <div className="be-tabs__strip" contentEditable={false}>
        {titles.map((title, i) => (
          <button
            key={i}
            className={`be-tabs__tab${i === current ? ' is-active' : ''}`}
            onClick={() => setActive(i)}
            type="button"
          >
            {title}
            {i === opensOn && titles.length > 1 && (
              /* A dot, and what it meant was in a `title` on the span
                 carrying it — a mouse affordance on an element that takes no
                 focus. The tab button around it does take focus, so the words
                 go into its accessible name, where they cost nothing and
                 reach everybody. */
              <span className="be-tabs__default">
                ·<span className="sr-only"> (readers see this tab first)</span>
              </span>
            )}
          </button>
        ))}
        {editor.isEditable && (
          <button className="be-tabs__add" onClick={addTab} type="button" data-tip="Add tab">
            +
          </button>
        )}
        {/*
          The question this block asks, when the page asks it more than once.
          Named, and every block with that name follows the reader's choice —
          which is what "which package manager?" should cost a reader: one
          answer. Empty is the ordinary case and adds nothing to the file, so
          the field sits at the end of the strip rather than above it.
        */}
        {editor.isEditable && (
          <input
            className="be-inline-input be-tabs__group"
            value={(node.attrs['group'] as string | null) ?? ''}
            placeholder="same question as…"
            data-tip="Give two tab blocks the same name and choosing in one chooses in both"
            onChange={(e) => updateAttributes({ group: e.target.value.trim() || null })}
          />
        )}
      </div>
      <NodeViewContent />
    </NodeViewWrapper>
  )
}

/** One tab. Its title is edited here so the strip above stays in step. */
export function TabView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
  // The tab states its own index so CSS can match it by attribute rather than
  // by DOM position. TipTap wraps every nodeview in `div.react-renderer` and
  // wraps NodeViewContent's children in another unclassed div, so any
  // `nth-child` rule counts the wrong elements — which is exactly why no panel
  // was hidden. Attributes do not move when the wrapper depth changes.
  const pos = getPos?.()
  const index = pos === undefined ? 0 : editor.state.doc.resolve(pos).index()
  const remove = () => {
    const from = getPos?.()
    if (from === undefined) return
    editor
      .chain()
      .focus()
      .deleteRange({ from, to: from + node.nodeSize })
      .run()
  }
  return (
    <NodeViewWrapper className="be-tab" data-sls="tab" data-index={index}>
      <div className="be-tab__head" contentEditable={false}>
        <input
          className="be-inline-input"
          value={(node.attrs['title'] as string | null) ?? ''}
          placeholder="Tab title"
          disabled={!editor.isEditable}
          onChange={(e) => updateAttributes({ title: e.target.value || null })}
        />
        {editor.isEditable && (
          <input
            className="be-inline-input be-tab__value"
            value={(node.attrs['value'] as string | null) ?? ''}
            placeholder={(node.attrs['title'] as string | null) ?? 'value'}
            data-tip="What this tab is, for matching across blocks — npm, macos"
            onChange={(e) => updateAttributes({ value: e.target.value.trim() || null })}
          />
        )}
        {editor.isEditable && (
          <button className="be-icon-btn" onClick={remove} type="button" data-tip="Remove tab">
            ×
          </button>
        )}
      </div>
      <NodeViewContent />
    </NodeViewWrapper>
  )
}
